import {ReactNode, useEffect, useState} from 'react';
import {Account} from '../features/accounts/types';
import {fetch as fetchAccount} from '../features/accounts/services/AccountsService';
import {UserAccountContext} from '../context/UserAccountContext';
import {useUserAttributes} from '../hooks/useUserAttributes';

// Loads the signed-in operator's account record from console-api.
//
// ⚠️ THE ACCOUNT LIVES IN console-api, NOT IN THIS APP'S API.
// flexinet-users-api has no Account entity, so this is a deliberate
// cross-domain read of the one API that owns it.
//
// ⚠️ It keys on `custom:account_id`, never the Cognito `sub`. The two are
// structurally identical (both UUIDs, both ID!) and neither derives from
// the other — see `docs/API_ENTITY_SEAM_MAP.md` §2.
export const UserAccountProvider = ({children}: {children?: ReactNode}) => {
    const [userAccount, setUserAccount] = useState<Account | undefined>(undefined);

    const {accountId} = useUserAttributes();

    useEffect(() => {
        if (!accountId) {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const result = await fetchAccount(accountId);
                // Guard the write, not the fetch: a sign-out mid-flight
                // unmounts this provider, and setting state afterwards
                // warns in dev and leaks the previous user's account.
                if (!cancelled) {
                    setUserAccount(result ?? undefined);
                }
            } catch (error) {
                // A failed account read must not blank the app. The
                // context stays undefined and consumers render their
                // not-loaded branch.
                console.trace('USER ACCOUNT. FETCH ERROR', error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [accountId]);

    return (
        <UserAccountContext.Provider value={{userAccount}}>
            {children}
        </UserAccountContext.Provider>
    );
};
